import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Check, X, Download } from "lucide-react";

function Money({ v }) { return <>${Number(v || 0).toFixed(2)}</>; }

function SettingsPanel() {
  const [s, setS] = useState(null);
  const [msg, setMsg] = useState("");
  useEffect(() => { api.get("/admin/marketplace/settings").then(r => setS(r.data)); }, []);
  if (!s) return null;
  const save = async () => {
    const { key, updated_at, ...patch } = s; // eslint-disable-line no-unused-vars
    const { data } = await api.put("/admin/marketplace/settings", patch);
    setS(data); setMsg("Saved."); setTimeout(() => setMsg(""), 2000);
  };
  const num = (k, extra = {}) => (
    <label className="block"><span className="overline">{k.replace(/_/g, " ")}</span>
      <input type="number" step="0.1" className="input-base mt-2" value={s[k]} onChange={e => setS({...s, [k]: parseFloat(e.target.value)})} data-testid={`mk-setting-${k}`} {...extra}/></label>
  );
  const bool = (k, label) => (
    <label className="flex items-center gap-3 text-sm cursor-pointer">
      <input type="checkbox" checked={!!s[k]} onChange={e => setS({...s, [k]: e.target.checked})} data-testid={`mk-setting-${k}`}/> {label}
    </label>
  );
  return (
    <div className="card-flat p-6 space-y-5" data-testid="mk-settings">
      <div>
        <h3 className="font-display text-2xl">Commission &amp; fee rules</h3>
        <p className="text-sm text-[var(--ff-text-muted)]">Changes apply to every new order immediately — no code deploy needed.</p>
      </div>
      <div className="grid sm:grid-cols-3 gap-4">
        {num("default_commission_pct")}{num("min_commission_pct")}{num("max_commission_pct")}
        {num("payout_hold_days", { step: 1 })}{num("processing_fee_pct")}{num("processing_fee_fixed", { step: 0.01 })}
        {num("tax_rate_pct")}
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        {bool("platform_absorbs_processing_fees", "Farm To You absorbs payment-processing fees")}
        {bool("platform_absorbs_refunds", "Farm To You absorbs refunds")}
        {bool("platform_absorbs_shipping", "Farm To You absorbs shipping costs")}
        {bool("platform_absorbs_chargebacks", "Farm To You absorbs chargebacks")}
        {bool("require_product_approval", "New rancher products require admin approval")}
      </div>
      <div className="border-t border-[var(--ff-border)] pt-5">
        <p className="overline mb-1">Platform safety limits</p>
        <p className="text-xs text-[var(--ff-text-muted)] mb-3">Max shipping-label spend per rancher per day (the platform absorbs label cost). Resets at midnight UTC. Set per-rancher overrides under &quot;Rancher rates.&quot;</p>
        <div className="grid sm:grid-cols-3 gap-4">
          {num("label_daily_cap", { step: 10, min: 0 })}
          {num("label_cap_daily_bump", { step: 10, min: 0 })}
        </div>
        <p className="text-[11px] text-[var(--ff-text-muted)] mt-2">&quot;Daily cap&quot; is the normal per-rancher limit; &quot;daily bump&quot; is how much a one-click &quot;Raise my limit&quot; link (in cap-alert emails) or an admin grant adds for the rest of that day.</p>
      </div>
      <div className="border-t border-[var(--ff-border)] pt-5">
        <p className="overline mb-1">Ambassador communications</p>
        <p className="text-xs text-[var(--ff-text-muted)] mb-3">Keep this OFF until your Twilio A2P 10DLC campaign is <strong>approved</strong>. Flip it on to start sending the welcome text to newly approved ambassadors — no redeploy needed.</p>
        {bool("ambassador_welcome_sms_enabled", "Send welcome SMS to new ambassadors")}
      </div>
      <label className="block"><span className="overline">Multi-rancher shipping notice (shown at checkout)</span>
        <input className="input-base mt-2" value={s.multi_rancher_shipping_notice} onChange={e => setS({...s, multi_rancher_shipping_notice: e.target.value})} data-testid="mk-setting-notice"/></label>
      <div className="flex items-center gap-3">
        <button onClick={save} className="btn-primary" data-testid="mk-settings-save">Save settings</button>
        {msg && <span className="text-sm text-emerald-700" data-testid="mk-settings-saved">{msg}</span>}
      </div>
    </div>
  );
}

function VendorRates() {
  const [rows, setRows] = useState([]);
  const load = () => api.get("/admin/marketplace/vendors").then(r => setRows(r.data));
  useEffect(() => { load(); }, []);
  const set = async (id, val) => {
    await api.put(`/admin/marketplace/vendors/${id}/commission`, { commission_pct: val === "" ? null : parseFloat(val) });
    load();
  };
  const setCap = async (id, val) => {
    await api.put(`/admin/marketplace/vendors/${id}/label-cap`, { label_daily_cap: val === "" ? null : parseFloat(val) });
    load();
  };
  return (
    <div className="card-flat p-6" data-testid="mk-vendor-rates">
      <h3 className="font-display text-2xl">Per-rancher rates &amp; limits</h3>
      <p className="text-sm text-[var(--ff-text-muted)] mb-4">Leave blank to use the global default. Enter a negotiated commission % or a custom daily label-spend cap ($) to override.</p>
      <table className="w-full text-sm">
        <thead className="text-left bg-[var(--ff-muted)]"><tr><th className="p-2">Rancher</th><th className="p-2">Status</th><th className="p-2">Effective %</th><th className="p-2">Commission override</th><th className="p-2">Daily label cap</th><th className="p-2">Cap override ($)</th></tr></thead>
        <tbody>{rows.map(v => (
          <tr key={v.id} className="border-t border-[var(--ff-border)]" data-testid={`mk-vendor-${v.id}`}>
            <td className="p-2">{v.farm_name}</td><td className="p-2 text-xs">{v.vendor_status}</td>
            <td className="p-2 font-semibold" data-testid={`mk-vendor-eff-${v.id}`}>{v.effective_commission_pct}%</td>
            <td className="p-2"><input type="number" step="0.5" className="input-base w-24 py-1" defaultValue={v.commission_pct ?? ""} onBlur={e => set(v.id, e.target.value)} data-testid={`mk-vendor-input-${v.id}`}/></td>
            <td className="p-2 font-semibold" data-testid={`mk-vendor-eff-cap-${v.id}`}>${Number(v.effective_label_cap || 0).toFixed(0)}</td>
            <td className="p-2"><input type="number" step="10" min="0" placeholder="default" className="input-base w-24 py-1" defaultValue={v.label_daily_cap ?? ""} onBlur={e => setCap(v.id, e.target.value)} data-testid={`mk-vendor-cap-input-${v.id}`}/></td>
          </tr>))}</tbody>
      </table>
    </div>
  );
}

function ProductApprovals() {
  const [data, setData] = useState({ counts: {}, products: [] });
  const [status, setStatus] = useState("pending");
  const load = () => api.get("/admin/marketplace/products", { params: { status } }).then(r => setData(r.data));
  useEffect(() => { load(); }, [status]); // eslint-disable-line
  const act = async (id, verb) => { await api.post(`/admin/marketplace/products/${id}/${verb}`, {}); load(); };
  return (
    <div className="card-flat p-6" data-testid="mk-product-approvals">
      <h3 className="font-display text-2xl">Product approvals</h3>
      <div className="flex gap-2 my-4">
        {["pending", "approved", "rejected"].map(s => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`mk-products-filter-${s}`}
            className={`px-4 py-1.5 rounded-full text-xs border capitalize ${status === s ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]" : "border-[var(--ff-border)]"}`}>
            {s} ({data.counts[s] ?? 0})
          </button>))}
      </div>
      {data.products.length === 0 && <p className="text-sm text-[var(--ff-text-muted)] py-6 text-center">Nothing here.</p>}
      <div className="space-y-2">
        {data.products.map(p => (
          <div key={p.id} className="flex items-center gap-4 border border-[var(--ff-border)] rounded-lg p-3" data-testid={`mk-product-${p.id}`}>
            {p.image_url && <img src={p.image_url} alt="" className="w-12 h-12 rounded object-cover"/>}
            <div className="flex-1 min-w-0">
              <p className="font-medium truncate">{p.name}</p>
              <p className="text-xs text-[var(--ff-text-muted)]">{p.vendor_name} · {p.category} · ${Number(p.price).toFixed(2)} / {p.unit}</p>
            </div>
            {status !== "approved" && <button onClick={() => act(p.id, "approve")} className="btn-primary text-xs" data-testid={`mk-approve-${p.id}`}><Check size={14}/> Approve</button>}
            {status !== "rejected" && <button onClick={() => act(p.id, "reject")} className="btn-secondary text-xs" data-testid={`mk-reject-${p.id}`}><X size={14}/> Reject</button>}
          </div>))}
      </div>
    </div>
  );
}

function PriceOverview() {
  const [rows, setRows] = useState([]);
  const [q, setQ] = useState("");
  const [savingId, setSavingId] = useState("");
  const [msg, setMsg] = useState("");
  const [msgErr, setMsgErr] = useState(false);
  const load = () => api.get("/admin/marketplace/products", { params: { limit: 300 } }).then(r => setRows(r.data.products || []));
  useEffect(() => { load(); }, []);
  const saveMarkup = async (id, val, el) => {
    const pct = val === "" ? null : parseFloat(val);
    if (pct === null || isNaN(pct)) return;
    setSavingId(id); setMsg("");
    try {
      const { data } = await api.put(`/admin/marketplace/products/${id}/markup`, { markup_pct: pct });
      setRows(rs => rs.map(r => r.id === id ? { ...r, markup_pct: data.markup_pct, price: data.price } : r));
      if (el) el.value = data.markup_pct ?? "";
      setMsgErr(false); setMsg("Updated."); setTimeout(() => setMsg(""), 2000);
    } catch (err) {
      const prev = rows.find(r => r.id === id);
      if (el && prev) el.value = prev.markup_pct ?? "";
      setMsgErr(true); setMsg(err.response?.data?.detail || "Could not update."); setTimeout(() => setMsg(""), 4000);
    } finally { setSavingId(""); }
  };
  const term = q.trim().toLowerCase();
  const filtered = term ? rows.filter(r => (r.name || "").toLowerCase().includes(term) || (r.vendor_name || "").toLowerCase().includes(term)) : rows;
  const withWholesale = filtered.filter(r => r.wholesale_cost != null && r.wholesale_cost !== "" && Number(r.wholesale_cost) > 0);
  const noWholesale = filtered.filter(r => !(r.wholesale_cost != null && r.wholesale_cost !== "" && Number(r.wholesale_cost) > 0));
  return (
    <div className="card-flat p-6" data-testid="mk-price-overview">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h3 className="font-display text-2xl">Price overview</h3>
        {msg && <span className={`text-xs ${msgErr ? "text-rose-700" : "text-emerald-700"}`} data-testid="mk-price-msg">{msg}</span>}
      </div>
      <p className="text-sm text-[var(--ff-text-muted)] mb-4">Audit product economics across the catalog: what the rancher is paid (wholesale), your markup, and what the member pays. Edit a markup % to re-price instantly.</p>
      <input className="input-base mb-4 max-w-sm py-1.5 text-sm" placeholder="Search product or rancher…" value={q} onChange={e => setQ(e.target.value)} data-testid="mk-price-search"/>
      {withWholesale.length === 0 && noWholesale.length === 0 && <p className="text-sm text-[var(--ff-text-muted)] py-6 text-center">No products found.</p>}
      {withWholesale.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="text-left bg-[var(--ff-muted)]"><tr>
              <th className="p-2">Product</th><th className="p-2">Rancher</th>
              <th className="p-2 text-right">Wholesale</th><th className="p-2 text-right">Markup %</th>
              <th className="p-2 text-right">Member price</th><th className="p-2 text-right">Margin $</th>
            </tr></thead>
            <tbody>{withWholesale.map(p => {
              const ws = Number(p.wholesale_cost || 0);
              const price = Number(p.price || 0);
              const margin = price - ws;
              return (
                <tr key={p.id} className="border-t border-[var(--ff-border)]" data-testid={`mk-price-row-${p.id}`}>
                  <td className="p-2">
                    <div className="flex items-center gap-2">
                      {p.image_url && <img src={p.image_url} alt="" className="w-8 h-8 rounded object-cover"/>}
                      <span className="font-medium truncate max-w-[180px]">{p.name}</span>
                    </div>
                  </td>
                  <td className="p-2 text-xs text-[var(--ff-text-muted)]">{p.vendor_name || "—"}</td>
                  <td className="p-2 text-right"><Money v={ws}/></td>
                  <td className="p-2 text-right">
                    <input type="number" step="1" min="0" className="input-base !w-20 py-1 text-right" defaultValue={p.markup_pct ?? ""}
                      disabled={savingId === p.id}
                      onBlur={e => { if (e.target.value !== String(p.markup_pct ?? "")) saveMarkup(p.id, e.target.value, e.target); }}
                      data-testid={`mk-price-markup-${p.id}`}/>
                  </td>
                  <td className="p-2 text-right font-semibold text-[var(--ff-primary)]" data-testid={`mk-price-member-${p.id}`}><Money v={price}/></td>
                  <td className="p-2 text-right text-emerald-700" data-testid={`mk-price-margin-${p.id}`}><Money v={margin}/></td>
                </tr>
              );
            })}</tbody>
          </table>
        </div>
      )}
      {noWholesale.length > 0 && (
        <div className="mt-6">
          <p className="overline mb-2">Legacy / no wholesale set ({noWholesale.length})</p>
          <p className="text-xs text-[var(--ff-text-muted)] mb-2">These use the old % commission model. Set a wholesale cost in the rancher&apos;s product editor to switch them to markup pricing.</p>
          <div className="space-y-1">
            {noWholesale.map(p => (
              <div key={p.id} className="flex items-center justify-between text-sm border-t border-[var(--ff-border)] py-1.5" data-testid={`mk-price-legacy-${p.id}`}>
                <span className="truncate">{p.name} <span className="text-xs text-[var(--ff-text-muted)]">· {p.vendor_name || "—"}</span></span>
                <span className="text-[var(--ff-text-muted)]">Member price <Money v={p.price}/></span>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function FinanceOverview() {
  const [f, setF] = useState(null);
  const [subs, setSubs] = useState([]);
  const [payoutMsg, setPayoutMsg] = useState("");
  const load = () => { api.get("/admin/marketplace/finance").then(r => setF(r.data)); api.get("/admin/marketplace/suborders", { params: { limit: 50 } }).then(r => setSubs(r.data)); };
  useEffect(() => { load(); }, []);
  const runPayouts = async () => {
    setPayoutMsg("Running…");
    const { data } = await api.post("/admin/marketplace/payouts/run");
    setPayoutMsg(`Released ${data.released} (${data.stripe} via Stripe · ${data.ledger} ledger). ${data.not_due} not yet due.`);
    load(); setTimeout(() => setPayoutMsg(""), 6000);
  };
  if (!f) return null;
  const cards = [["Gross marketplace sales", f.gross_marketplace_sales], ["Commissions earned", f.commissions_earned], ["Membership revenue", f.membership_revenue], ["Rancher payouts pending", f.payout_pending], ["Payouts released", f.payout_paid]];
  const setStatus = async (id, st) => { await api.post(`/admin/marketplace/suborders/${id}/status`, { status: st }); load(); };
  return (
    <div className="space-y-6" data-testid="mk-finance">
      <div className="grid sm:grid-cols-3 lg:grid-cols-5 gap-3">
        {cards.map(([l, v]) => <div key={l} className="card-flat p-4"><p className="overline">{l}</p><p className="font-display text-2xl mt-1 text-[var(--ff-primary)]"><Money v={v}/></p></div>)}
      </div>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="card-flat p-5"><p className="overline mb-2">Top ranchers</p>
          {f.top_vendors.length === 0 && <p className="text-sm text-[var(--ff-text-muted)]">No sales yet.</p>}
          {f.top_vendors.map(v => <div key={v.vendor_id} className="flex justify-between text-sm py-1"><span>{v.vendor_name}</span><span><Money v={v.gross}/> · fee <Money v={v.commission}/></span></div>)}</div>
        <div className="card-flat p-5"><p className="overline mb-2">Top products</p>
          {f.top_products.length === 0 && <p className="text-sm text-[var(--ff-text-muted)]">No sales yet.</p>}
          {f.top_products.map(p => <div key={p.product_id} className="flex justify-between text-sm py-1"><span>{p.name} <span className="text-[var(--ff-text-muted)]">· {p.vendor_name}</span></span><span>{p.units} sold · <Money v={p.revenue}/></span></div>)}</div>
      </div>
      <div className="card-flat p-5">
        <div className="flex items-center justify-between mb-3">
          <p className="overline">Vendor suborders</p>
          <div className="flex items-center gap-3">
            {payoutMsg && <span className="text-xs text-emerald-700" data-testid="mk-payout-msg">{payoutMsg}</span>}
            <button onClick={runPayouts} className="btn-secondary text-xs" data-testid="mk-run-payouts">Run payouts now</button>
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/admin/marketplace/ledger.csv`} className="text-xs underline flex items-center gap-1" data-testid="mk-ledger-csv"><Download size={12}/> Ledger CSV</a>
          </div>
        </div>
        <p className="text-xs text-[var(--ff-text-muted)] mb-3">Payouts auto-release {f.settings?.payout_hold_days ?? 7} days after delivery — via Stripe Connect transfer when the rancher is onboarded, otherwise to the ledger. Use &quot;Run payouts now&quot; to release any that are due.</p>
        {subs.length === 0 && <p className="text-sm text-[var(--ff-text-muted)]">No suborders yet.</p>}
        <div className="space-y-2">{subs.map(s => (
          <div key={s.id} className="flex flex-wrap items-center gap-3 text-sm border-t border-[var(--ff-border)] pt-2" data-testid={`mk-suborder-${s.id}`}>
            <span className="font-mono text-xs">{s.id}</span><span className="flex-1">{s.vendor_name}</span>
            <span><Money v={s.subtotal}/> → payout <Money v={s.payout}/></span>
            <span className="px-2 py-0.5 rounded-full bg-[var(--ff-muted)] text-xs">{s.status}</span>
            <span className="text-xs text-[var(--ff-text-muted)]">{s.payout_status}</span>
            {s.status === "delivered" && s.payout_status === "pending" && <button className="btn-primary text-xs" onClick={async () => { await api.post(`/admin/marketplace/suborders/${s.id}/mark-paid`); load(); }} data-testid={`mk-markpaid-${s.id}`}>Mark paid</button>}
            {!["refunded", "canceled"].includes(s.status) && <button className="text-xs underline" onClick={() => setStatus(s.id, "refunded")} data-testid={`mk-refund-${s.id}`}>Refund</button>}
            {s.status !== "disputed" && <button className="text-xs underline" onClick={() => setStatus(s.id, "disputed")} data-testid={`mk-dispute-${s.id}`}>Dispute</button>}
          </div>))}</div>
      </div>
    </div>
  );
}

function DropRequests() {
  const [data, setData] = useState({ counts: {}, drops: [] });
  const [status, setStatus] = useState("pending");
  const [reviewing, setReviewing] = useState(null);
  const [notes, setNotes] = useState("");
  const load = () => api.get("/admin/marketplace/drop-requests", { params: { status } }).then(r => setData(r.data));
  useEffect(() => { load(); }, [status]); // eslint-disable-line
  const act = async (slug, verb) => { await api.post(`/admin/marketplace/drop-requests/${slug}/${verb}`, { notes: notes || null }); setReviewing(null); setNotes(""); load(); };
  return (
    <div className="card-flat p-6" data-testid="mk-drop-requests">
      <h3 className="font-display text-2xl">Rancher drop requests</h3>
      <p className="text-sm text-[var(--ff-text-muted)] mt-1">Ranchers propose limited drops; approve to make them live to members.</p>
      <div className="flex gap-2 my-4">
        {["pending", "approved", "rejected"].map(s => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`mk-dr-filter-${s}`}
            className={`px-4 py-1.5 rounded-full text-xs border capitalize ${status === s ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]" : "border-[var(--ff-border)]"}`}>
            {s} ({data.counts[s] ?? 0})
          </button>))}
      </div>
      {data.drops.length === 0 && <p className="text-sm text-[var(--ff-text-muted)] py-6 text-center">Nothing here.</p>}
      <div className="space-y-2">
        {data.drops.map(d => (
          <div key={d.id} className="border border-[var(--ff-border)] rounded-lg p-4" data-testid={`mk-dr-${d.slug}`}>
            <div className="flex items-center gap-4">
              {d.image_url && <img src={d.image_url} alt="" className="w-12 h-12 rounded object-cover"/>}
              <div className="flex-1 min-w-0">
                <p className="font-medium truncate">{d.name} <span className="text-[var(--ff-text-muted)] text-xs">· {d.farm_name}</span></p>
                <p className="text-xs text-[var(--ff-text-muted)]">${Number(d.price).toFixed(2)} · {d.total_inventory} units · drops {(d.drops_at || "").slice(0, 10)}</p>
              </div>
              {status === "pending" && <>
                <button onClick={() => act(d.slug, "approve")} className="btn-primary text-xs" data-testid={`mk-dr-approve-${d.slug}`}><Check size={14}/> Approve</button>
                <button onClick={() => setReviewing(d.slug)} className="btn-secondary text-xs" data-testid={`mk-dr-reject-${d.slug}`}><X size={14}/> Reject</button>
              </>}
            </div>
            {reviewing === d.slug && (
              <div className="mt-3 flex gap-2">
                <input className="input-base flex-1 py-1 text-sm" placeholder="Reason / notes to rancher" value={notes} onChange={e => setNotes(e.target.value)} data-testid={`mk-dr-notes-${d.slug}`}/>
                <button onClick={() => act(d.slug, "reject")} className="btn-secondary text-xs">Send rejection</button>
              </div>
            )}
          </div>))}
      </div>
    </div>
  );
}

function Spotlight() {
  const [preview, setPreview] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [msg, setMsg] = useState("");
  const load = () => api.get("/admin/marketplace/spotlight/preview").then(r => setPreview(r.data));
  useEffect(() => { load(); }, []);
  const send = async (test) => {
    setMsg("Sending…");
    const { data } = await api.post("/admin/marketplace/spotlight/send-now", test ? { test_email: testEmail } : {});
    setMsg(data.skipped ? "No newly approved ranchers to feature yet." : `Sent to ${data.sent} of ${data.recipients} ${test ? "(test)" : "members"}.`);
    setTimeout(() => setMsg(""), 6000);
  };
  if (!preview) return null;
  return (
    <div className="card-flat p-6 space-y-4" data-testid="mk-spotlight">
      <div>
        <h3 className="font-display text-2xl">Rancher Spotlight email</h3>
        <p className="text-sm text-[var(--ff-text-muted)] mt-1">A monthly &quot;New on the marketplace&quot; email featuring ranchers approved in the last ~35 days. Auto-sends on the 1st at 10am ET; you can also send now.</p>
      </div>
      <div className="grid sm:grid-cols-2 gap-3">
        <div className="bg-[var(--ff-muted)] rounded-lg p-4"><p className="overline">Newly approved ranchers</p><p className="font-display text-3xl text-[var(--ff-primary)]" data-testid="mk-spotlight-count">{preview.ranchers.length}</p></div>
        <div className="bg-[var(--ff-muted)] rounded-lg p-4"><p className="overline">Member recipients</p><p className="font-display text-3xl text-[var(--ff-primary)]" data-testid="mk-spotlight-recipients">{preview.recipient_count}</p></div>
      </div>
      {preview.ranchers.length === 0 && <p className="text-sm text-[var(--ff-text-muted)]">No ranchers approved recently — the email won&apos;t send until a new rancher is approved.</p>}
      <div className="space-y-2">
        {preview.ranchers.map(r => (
          <div key={r.id} className="flex items-center gap-3 border border-[var(--ff-border)] rounded-lg p-3" data-testid={`mk-spotlight-rancher-${r.id}`}>
            {r.cover_image_url && <img src={r.cover_image_url} alt="" className="w-12 h-12 rounded object-cover"/>}
            <div className="flex-1 min-w-0"><p className="font-medium truncate">{r.farm_name}</p><p className="text-xs text-[var(--ff-text-muted)]">{r.location} · {r.products.length} cuts featured</p></div>
          </div>))}
      </div>
      <div className="flex flex-wrap items-center gap-3 border-t border-[var(--ff-border)] pt-4">
        <input className="input-base py-1 text-sm max-w-xs" placeholder="you@example.com" value={testEmail} onChange={e => setTestEmail(e.target.value)} data-testid="mk-spotlight-test-email"/>
        <button onClick={() => send(true)} disabled={!testEmail || preview.ranchers.length === 0} className="btn-secondary text-xs" data-testid="mk-spotlight-send-test">Send test</button>
        <button onClick={() => send(false)} disabled={preview.ranchers.length === 0} className="btn-primary text-xs" data-testid="mk-spotlight-send-all">Send to all members</button>
        {msg && <span className="text-sm text-emerald-700" data-testid="mk-spotlight-msg">{msg}</span>}
      </div>
    </div>
  );
}

function Refunds() {
  const [data, setData] = useState({ counts: {}, refunds: [] });
  const [status, setStatus] = useState("requested");
  const [notes, setNotes] = useState("");
  const [denying, setDenying] = useState(null);
  const load = () => api.get("/admin/marketplace/refunds", { params: { status } }).then(r => setData(r.data));
  useEffect(() => { load(); }, [status]); // eslint-disable-line
  const act = async (id, verb) => { await api.post(`/admin/marketplace/refunds/${id}/${verb}`, { notes: notes || null }); setDenying(null); setNotes(""); load(); };
  return (
    <div className="card-flat p-6" data-testid="mk-refunds">
      <h3 className="font-display text-2xl">Refund requests</h3>
      <p className="text-sm text-[var(--ff-text-muted)] mt-1">Ranchers flag orders for refund. Approving reverses the payout and marks the order refunded.</p>
      <div className="flex gap-2 my-4">
        {["requested", "approved", "denied"].map(s => (
          <button key={s} onClick={() => setStatus(s)} data-testid={`mk-refund-filter-${s}`}
            className={`px-4 py-1.5 rounded-full text-xs border capitalize ${status === s ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]" : "border-[var(--ff-border)]"}`}>
            {s} ({data.counts[s] ?? 0})
          </button>))}
      </div>
      {data.refunds.length === 0 && <p className="text-sm text-[var(--ff-text-muted)] py-6 text-center">Nothing here.</p>}
      <div className="space-y-2">
        {data.refunds.map(r => (
          <div key={r.id} className="border border-[var(--ff-border)] rounded-lg p-4" data-testid={`mk-refund-${r.id}`}>
            <div className="flex flex-wrap items-center gap-3">
              <div className="flex-1 min-w-0">
                <p className="font-medium">{r.vendor_name || r.vendor_id} · <span className="text-[var(--ff-primary)]"><Money v={r.amount}/></span></p>
                <p className="text-xs text-[var(--ff-text-muted)]">Order {(r.order_id || "").slice(0, 8)} · suborder {r.suborder_status || "—"}</p>
                <p className="text-sm mt-1">{r.reason}</p>
              </div>
              {status === "requested" && <>
                <button onClick={() => act(r.id, "approve")} className="btn-primary text-xs" data-testid={`mk-refund-approve-${r.id}`}><Check size={14}/> Approve &amp; refund</button>
                <button onClick={() => setDenying(r.id)} className="btn-secondary text-xs" data-testid={`mk-refund-deny-${r.id}`}><X size={14}/> Deny</button>
              </>}
            </div>
            {denying === r.id && (
              <div className="mt-3 flex gap-2">
                <input className="input-base flex-1 py-1 text-sm" placeholder="Reason for denial" value={notes} onChange={e => setNotes(e.target.value)} data-testid={`mk-refund-notes-${r.id}`}/>
                <button onClick={() => act(r.id, "deny")} className="btn-secondary text-xs">Confirm deny</button>
              </div>
            )}
          </div>))}
      </div>
    </div>
  );
}

function CapAlerts() {
  const [data, setData] = useState(null);
  const [days, setDays] = useState(1);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/marketplace/cap-alerts", { params: { days } }).then(r => setData(r.data)).catch(() => {});
  useEffect(() => { load(); }, [days]); // eslint-disable-line
  const grant = async (vid) => {
    setBusy(vid);
    try { await api.post(`/admin/marketplace/cap-alerts/${vid}/grant-bump`); await load(); }
    finally { setBusy(""); }
  };
  if (!data) return <p className="text-sm text-[var(--ff-text-muted)]">Loading cap alerts…</p>;
  const alerts = data.alerts || [];
  return (
    <div className="card-flat p-6" data-testid="mk-cap-alerts">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-1">
        <h3 className="font-display text-2xl">Shipping-label cap alerts</h3>
        <div className="flex gap-1.5">
          {[[1, "Today"], [3, "3 days"], [7, "7 days"]].map(([d, l]) => (
            <button key={d} onClick={() => setDays(d)} data-testid={`cap-alerts-range-${d}`}
              className={`px-3 py-1 rounded-full text-xs border ${days === d ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]" : "border-[var(--ff-border)]"}`}>{l}</button>
          ))}
        </div>
      </div>
      <p className="text-sm text-[var(--ff-text-muted)] mb-4">Ranchers who crossed 80% or 100% of their daily label limit. Grant a ${Number(data.daily_bump || 0).toFixed(0)} same-day bump so a busy shipping day never gets blocked.</p>
      {alerts.length === 0 && <p className="text-sm text-[var(--ff-text-muted)] py-8 text-center" data-testid="cap-alerts-empty">No cap alerts in this window. All ranchers are within their limits. 🎉</p>}
      {alerts.length > 0 && (
        <table className="w-full text-sm">
          <thead className="text-left bg-[var(--ff-muted)]"><tr><th className="p-2">Rancher</th><th className="p-2">Day</th><th className="p-2">Spent / cap</th><th className="p-2">Status</th><th className="p-2"></th></tr></thead>
          <tbody>{alerts.map(a => (
            <tr key={`${a.vendor_id}-${a.day}`} className="border-t border-[var(--ff-border)]" data-testid={`cap-alert-${a.vendor_id}`}>
              <td className="p-2 font-medium">{a.farm_name}{a.has_override && <span className="ml-1 text-[10px] text-[var(--ff-text-muted)]">(custom cap)</span>}</td>
              <td className="p-2 text-xs text-[var(--ff-text-muted)]">{a.day}</td>
              <td className="p-2">${a.spent.toFixed(2)} / ${a.cap.toFixed(2)}{a.bonus > 0 && <span className="text-[10px] text-emerald-700"> (+${a.bonus.toFixed(0)} bump)</span>}</td>
              <td className="p-2">
                {a.at_limit
                  ? <span className="inline-block px-2 py-0.5 rounded-full text-[11px] bg-rose-50 text-rose-700 border border-rose-200" data-testid={`cap-alert-status-${a.vendor_id}`}>At limit · {a.pct}%</span>
                  : <span className="inline-block px-2 py-0.5 rounded-full text-[11px] bg-amber-50 text-amber-800 border border-amber-200" data-testid={`cap-alert-status-${a.vendor_id}`}>{a.pct}%</span>}
              </td>
              <td className="p-2 text-right">
                <button onClick={() => grant(a.vendor_id)} disabled={busy === a.vendor_id} className="btn-secondary text-xs" data-testid={`cap-alert-grant-${a.vendor_id}`}>
                  {busy === a.vendor_id ? "Granting…" : `+$${Number(data.daily_bump || 0).toFixed(0)} today`}
                </button>
              </td>
            </tr>))}</tbody>
        </table>
      )}
    </div>
  );
}

export default function AdminMarketplaceTab() {
  const [sub, setSub] = useState("finance");
  const subs = [["finance", "Finance"], ["pricing", "Price overview"], ["settings", "Commission rules"], ["vendors", "Rancher rates"], ["cap-alerts", "Cap alerts"], ["products", "Product approvals"], ["drops", "Drop requests"], ["refunds", "Refunds"], ["spotlight", "Spotlight"]];
  return (
    <div className="space-y-6" data-testid="admin-marketplace-tab">
      <div className="flex gap-2 flex-wrap">
        {subs.map(([id, l]) => <button key={id} onClick={() => setSub(id)} data-testid={`mk-sub-${id}`}
          className={`px-4 py-1.5 rounded-full text-xs border ${sub === id ? "bg-[var(--ff-primary)] text-white border-[var(--ff-primary)]" : "border-[var(--ff-border)]"}`}>{l}</button>)}
      </div>
      {sub === "finance" && <FinanceOverview/>}
      {sub === "pricing" && <PriceOverview/>}
      {sub === "settings" && <SettingsPanel/>}
      {sub === "vendors" && <VendorRates/>}
      {sub === "cap-alerts" && <CapAlerts/>}
      {sub === "products" && <ProductApprovals/>}
      {sub === "drops" && <DropRequests/>}
      {sub === "refunds" && <Refunds/>}
      {sub === "spotlight" && <Spotlight/>}
    </div>
  );
}
